import { PerpsProvider } from '@kwenta/sdk/types'
import { type FC, useCallback, useId } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { CheckboxInput } from 'components/Checkbox'
import { Body } from 'components/Text'
import { FlexDivCentered } from 'components/layout/flex'
import {
	selectOneClickTradingSelected,
	selectUnlimitedApproval,
} from 'state/futures/common/selectors'
import { setPreferences } from 'state/futures/reducer'
import { useAppDispatch, useAppSelector } from 'state/hooks'

type Props = {
	provider?: PerpsProvider
}

const ApproveCheckbox: FC<Props> = ({ provider }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const id = useId()
	const unlimitedApproval = useAppSelector(selectUnlimitedApproval)
	const isOneClickSelected = useAppSelector(selectOneClickTradingSelected)

	const toggleUnlimitedApproval = useCallback(() => {
		dispatch(
			setPreferences({
				unlimitedApproval: !unlimitedApproval,
			})
		)
	}, [dispatch, unlimitedApproval])

	return (
		<Container disabled={isOneClickSelected}>
			<CheckboxInput
				type="checkbox"
				color="default"
				name={id}
				checked={unlimitedApproval}
				onChange={toggleUnlimitedApproval}
				variant="tick"
				id={id}
				style={{ flex: '0 0 20px' }}
			/>
			<Label htmlFor={id}>
				<Body size="medium" weight="bold">
					{t('futures.market.trade.confirmation.modal.approve-unlimited', {
						asset: provider === PerpsProvider.SNX_V2_OP ? 'sUSD' : 'USDC',
					})}
				</Body>
				<Badge>
					<p>{t(`futures.modals.trading-mode.${isOneClickSelected ? 'required' : 'suggested'}`)}</p>
				</Badge>
			</Label>
		</Container>
	)
}

const Container = styled(FlexDivCentered)<{ disabled?: boolean }>`
	gap: 10px;
	justify-content: flex-start;
	padding-bottom: 10px;
	opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`

const Label = styled.label`
	display: flex;
	align-items: center;
	flex: 1;
	cursor: pointer;
	gap: 10px;
`

const Badge = styled.span`
	display: flex;
	align-items: center;
	gap: 5px;
	padding: 5px 7px;
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.button.default.color};
	border-radius: 100px;
	height: 22px;
	font-size: 10px;
	line-height: 12px;
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.badge.yellow.background};
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.badge.yellow.dark.background};
`

export default ApproveCheckbox
